import { useState, useEffect, useRef } from 'react';
import { Box, Button, Typography, Alert, Paper } from '@mui/material';
import { Html5Qrcode } from 'html5-qrcode';
import { 
  hasStoredPermission, 
  storePermission, 
  clearStoredPermission,
  getPermissionInstructions,
  shouldShowPermissionTips 
} from '../utils/cameraPermissions';

// Native video-based barcode scanner for better mobile compatibility
export default function BarcodeScannerNative({ 
  onScanSuccess, 
  onScanError,
  continuous = true, // Default to continuous for mobile
  width = "100%",
  height = "300px",
  autoStart = true
}) {
  const [error, setError] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const startAttempts = useRef(0);
  const lastScanRef = useRef(null);
  const lastScanTimeRef = useRef(0);

  // Don't use useBarcodeScanner here - let parent component handle lookup
  // const { handleScan, isLoading, error: lookupError } = useBarcodeScanner(onScanSuccess);

  // Check if we're on Safari
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  // Comprehensive cleanup function
  const cleanup = () => {
    try {
      console.log('BarcodeScannerNative cleanup started');
      
      // Clear scanning interval
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
        scanIntervalRef.current = null;
      }
      
      // Stop camera stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => {
          track.stop();
          console.log('Camera track stopped:', track.label);
        });
        streamRef.current = null;
      }
      
      // Clear video element
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      
      // Clear HTML5 QR code instance
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.clear();
        html5QrCodeRef.current = null;
      }
      
      // Reset state
      setScanning(false);
      setCameraReady(false);
      setError(null);
      
      console.log('BarcodeScannerNative cleanup complete - camera fully stopped');
    } catch (err) {
      console.error('Error during cleanup:', err);
    }
  };

  // Check for stored permission and auto-start if granted
  useEffect(() => {
    // Check if permission was previously granted
    const checkStoredPermission = async () => {
      try {
        // Check stored permission state using utility
        if (hasStoredPermission()) {
          // Try to access camera directly if stored permission exists
          try {
            const stream = await navigator.mediaDevices.getUserMedia({
              video: { facingMode: 'environment' }
            });
            // If successful, permission is still granted
            stream.getTracks().forEach(track => track.stop()); // Clean up test stream
            setHasPermission(true);
            
            // Auto-start camera if permission is already granted
            if (autoStart) {
              setTimeout(() => startCamera(), 100);
            }
          } catch (permErr) {
            // Permission was revoked, clear stored state
            clearStoredPermission();
            setHasPermission(false);
          }
        }
      } catch (err) {
        // Ignore permission check errors
        console.log('Permission check not supported or failed');
      }
    };

    checkStoredPermission();
    
    // Stop camera when tab is hidden
    const handleVisibilityChange = () => {
      if (document.hidden && scanning) {
        console.log('Tab hidden - stopping camera');
        stopCamera();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      cleanup();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const startCamera = async () => {
    try {
      console.log('🔍 BarcodeScannerNative: startCamera called');
      setError(null);
      startAttempts.current += 1;
      
      // Request camera with mobile-friendly constraints
      const constraints = {
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      };
      
      console.log('🔍 Native: Getting camera stream...');
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      console.log('🔍 Native: Camera stream obtained');
      
      // Set video source
      if (videoRef.current) {
        console.log('🔍 Native: Setting video source');
        videoRef.current.srcObject = stream;
        
        // Important: Set these attributes for iOS Safari
        videoRef.current.setAttribute('autoplay', 'true');
        videoRef.current.setAttribute('muted', 'true');
        videoRef.current.setAttribute('playsinline', 'true');
        
        // Wait for video to be ready
        await new Promise((resolve) => {
          videoRef.current.onloadedmetadata = () => {
            resolve();
          };
          // Also resolve after a delay to prevent hanging
          setTimeout(resolve, 2000);
        });
        
        // Play the video
        console.log('🔍 Native: Playing video...');
        await videoRef.current.play();
        console.log('🔍 Native: Video playing successfully');
        
        setHasPermission(true);
        setScanning(true);
        setCameraReady(true);
        startAttempts.current = 0; // Reset attempts on success
        console.log('🔍 Native: Camera ready, starting scanning');
        
        // Store permission for future sessions using utility
        storePermission();
        
        // Start scanning
        startScanning();
      }
    } catch (err) {
      console.log('🔍 Native: Camera error:', err.name, err.message);
      // Only show error for permission denied
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Camera permission denied. Please allow camera access to scan barcodes.');
      } else {
        console.error('🔍 Native: Unexpected camera error:', err);
      }
      setScanning(false);
      setCameraReady(false);
    }
  };

  const startScanning = () => {
    if (!videoRef.current) return;
    
    console.log('🔍 Native: Starting scanning logic');
    // Create Html5Qrcode instance with explicit format support
    const html5QrCode = new Html5Qrcode("dummy-element", {
      // Enable all common barcode formats
      formatsToSupport: [
        0,  // QR_CODE
        1,  // AZTEC
        2,  // CODABAR
        3,  // CODE_39
        4,  // CODE_93
        5,  // CODE_128
        6,  // DATA_MATRIX
        7,  // MAXICODE
        8,  // ITF
        9,  // EAN_13
        10, // EAN_8
        11, // PDF_417
        12, // RSS_14
        13, // RSS_EXPANDED
        14, // UPC_A
        15, // UPC_E
        16  // UPC_EAN_EXTENSION
      ],
      verbose: false
    });
    html5QrCodeRef.current = html5QrCode;
    
    // Scan every 500ms
    console.log('🔍 Native: Setting up scan interval');
    scanIntervalRef.current = setInterval(async () => {
      if (!videoRef.current || videoRef.current.paused || videoRef.current.ended) {
        return;
      }
      
      try {
        // Create canvas to capture frame - focus on center region where scan box is
        const canvas = document.createElement('canvas');
        const fullWidth = videoRef.current.videoWidth;
        const fullHeight = videoRef.current.videoHeight;
        
        // Crop to center region (where the green scan box is)
        const cropWidth = Math.min(fullWidth * 0.8, 400); // 80% of width or max 400px
        const cropHeight = Math.min(fullHeight * 0.4, 200); // 40% of height or max 200px
        const cropX = (fullWidth - cropWidth) / 2;
        const cropY = (fullHeight - cropHeight) / 2;
        
        canvas.width = cropWidth;
        canvas.height = cropHeight;
        const ctx = canvas.getContext('2d');
        
        // Draw the cropped region
        ctx.drawImage(
          videoRef.current,
          cropX, cropY, cropWidth, cropHeight, // Source crop
          0, 0, cropWidth, cropHeight          // Destination
        );
        
        // Enhance contrast for better barcode detection
        const imageData = ctx.getImageData(0, 0, cropWidth, cropHeight);
        const data = imageData.data;
        
        // Simple contrast enhancement
        for (let i = 0; i < data.length; i += 4) {
          // Convert to grayscale and enhance contrast
          const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
          const enhanced = gray > 128 ? 255 : 0; // High contrast black/white
          data[i] = enhanced;     // R
          data[i + 1] = enhanced; // G
          data[i + 2] = enhanced; // B
          // Alpha stays the same
        }
        
        ctx.putImageData(imageData, 0, 0);
        
        // Reduce scanning logs now that it's working
        if (Date.now() % 30000 < 500) { // Log every ~30 seconds
          console.log('🔍 Native: Scanning active...', { 
            cropSize: `${canvas.width}x${canvas.height}`
          });
        }
        
        // Convert to blob
        canvas.toBlob(async (blob) => {
          if (!blob) return;
          
          try {
            const file = new File([blob], "frame.png", { type: "image/png" });
            const result = await html5QrCode.scanFile(file, false);
            
            if (result) {
              console.log('🔍 Native: Barcode detected!', result);
              // Prevent duplicate scans of the same barcode within 5 seconds
              const now = Date.now();
              const timeSinceLastScan = now - lastScanTimeRef.current;
              
              if (lastScanRef.current === result && timeSinceLastScan < 5000) {
                // Same barcode scanned within 5 seconds, ignore
                return;
              }
              
              // Update last scan tracking
              lastScanRef.current = result;
              lastScanTimeRef.current = now;
              
              // Call parent component's success handler directly
              if (onScanSuccess) {
                await onScanSuccess(result);
              }
              
              // Handle continuous vs single scan mode
              if (continuous) {
                // Continue scanning for multiple items - don't stop camera
                // No need to restart scanning, just continue with next frame
              } else {
                // Stop scanning after single scan
                stopCamera();
              }
            }
          } catch (err) {
            // Reduce error logging now that scanning works
            // These errors are normal when no barcode is in view
          }
        }, 'image/png');
      } catch (err) {
        // Ignore frame capture errors
      }
    }, 500);
  };

  const stopCamera = () => {
    cleanup();
    setScanning(false);
  };

  return (
    <Box>
      <Paper 
        elevation={3} 
        sx={{ 
          width, 
          height, 
          overflow: 'hidden',
          position: 'relative',
          backgroundColor: '#000'
        }}
      >
        <video
          ref={videoRef}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: hasPermission ? 'block' : 'none'
          }}
          autoPlay
          muted
          playsInline
        />
        
        {/* Show start button when camera isn't active */}
        {!cameraReady && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              zIndex: 10
            }}
          >
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={startCamera}
              sx={{ 
                backgroundColor: '#1976d2',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#1565c0'
                },
                fontSize: '18px',
                padding: '12px 32px'
              }}
            >
              Start Camera
            </Button>
            {shouldShowPermissionTips() && (
              <Box sx={{ 
                mt: 2, 
                px: 2,
                backgroundColor: 'rgba(255,255,255,0.9)',
                borderRadius: 1,
                py: 1,
                maxWidth: '300px'
              }}>
                <Typography variant="body2" sx={{ textAlign: 'center', mb: 1, color: '#333' }}>
                  💡 Camera Permission Tips:
                </Typography>
                {getPermissionInstructions().instructions.map((tip, index) => (
                  <Typography key={index} variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#555', mb: 0.5 }}>
                    • {tip}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        )}
        
        {scanning && cameraReady && (
          <>
            {/* Overlay with cutout */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.3)',
                pointerEvents: 'none'
              }}
            />
            {/* Scan box */}
            <Box
              sx={{
                position: 'absolute',
                top: '45%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '280px',
                height: '140px',
                border: '3px solid #00ff00',
                borderRadius: '12px',
                backgroundColor: 'transparent',
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: -3,
                  left: -3,
                  right: -3,
                  bottom: -3,
                  border: '3px solid rgba(0,255,0,0.3)',
                  borderRadius: '12px'
                }
              }}
            />
            <Typography 
              variant="body2" 
              sx={{ 
                position: 'absolute',
                bottom: '20%',
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'white',
                textAlign: 'center',
                textShadow: '0 1px 3px rgba(0,0,0,0.8)'
              }}
            >
              {'Align barcode within green box'}
            </Typography>
          </>
        )}
      </Paper>
      
      {/* Show lookup errors */}
      {/* Lookup errors are handled by parent component */}
      
      {/* Removed custom Stop Scanner button - using Html5Qrcode's built-in stop button instead
      {scanning && (
        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="secondary"
            fullWidth
            onClick={stopCamera}
          >
            Stop Scanner
          </Button>
        </Box>
      )} */}
      
      {/* Hidden element for Html5Qrcode */}
      <div id="dummy-element" style={{ display: 'none' }} />
    </Box>
  );
}